import { getTFLiteModelFileName, SegmentationConfig } from "../helpers/segmentationHelper";

export type InferenceWorker = {
  // Post a frame for inference. Fire-and-forget; drops the frame if the worker is still busy.
  infer(pixels: Uint8Array, width: number, height: number): void;
  // Latest mask returned by the worker, or null until the first result arrives.
  getLatestMask(): { data: Float32Array; offset: number } | null;
  terminate(): void;
};

// Creates the inference worker and resolves once it has loaded the model (or rejects on failure,
// so the caller can fall back to inline main-thread inference).
export async function useInferenceWorker(
  segmentationConfig: SegmentationConfig,
  simd: boolean,
  timeoutMs = 8000,
): Promise<InferenceWorker> {
  const worker = new Worker(new URL("../pipelines/webgl2/inferenceWorker", import.meta.url), { type: "classic" });

  const modelFileName = getTFLiteModelFileName(segmentationConfig.model, segmentationConfig.inputResolution);
  const modelUrl = `/models/${modelFileName}.tflite`;

  let latestMask: Float32Array | null = null;
  let busy = false;

  worker.onmessage = (event: MessageEvent) => {
    if (event.data?.type === "result") {
      latestMask = new Float32Array(event.data.data);
      busy = false;
    }
  };

  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      worker.removeEventListener("message", onInit);
      reject(new Error("inference worker init timeout"));
    }, timeoutMs);

    function onInit(event: MessageEvent) {
      if (event.data?.type === "ready") {
        window.clearTimeout(timer);
        worker.removeEventListener("message", onInit);
        resolve();
      } else if (event.data?.type === "error") {
        window.clearTimeout(timer);
        worker.removeEventListener("message", onInit);
        reject(new Error(event.data.error));
      }
    }

    worker.addEventListener("message", onInit);
    worker.postMessage({ type: "init", simd, modelUrl });
  });

  return {
    infer(pixels: Uint8Array, width: number, height: number) {
      if (busy) return; // drop this frame; keep rendering with the last mask (graceful under load)
      busy = true;
      // Clone so the caller can keep reusing its `pixels` buffer; transfer the copy to avoid a second copy.
      const copy = pixels.slice();
      worker.postMessage({ type: "infer", pixels: copy.buffer, width, height }, [copy.buffer]);
    },
    getLatestMask() {
      return latestMask ? { data: latestMask, offset: 0 } : null;
    },
    terminate() {
      worker.terminate();
    },
  };
}
